
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { StoreProvider } from './context/StoreContext';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { CartProvider } from './context/CartContext';
import ProtectedRoute from './components/ProtectedRoute';

// SaaS & Auth Pages
import SaaSHomePage from './pages/saas/SaaSHomePage';
import LoginPage from './pages/auth/LoginPage';
import SetupRestaurant from './pages/admin/SetupRestaurant';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import SitemapPage from './pages/saas/SitemapPage';

// Admin Pages
import AdminLayout from './pages/admin/AdminLayout';
import ManagementAI from './pages/admin/ManagementAI'; // Dashboard / Relatórios
import MenuManager from './pages/admin/MenuManager';
import OrdersKanban from './pages/admin/OrdersKanban';
import MarketingPage from './pages/admin/MarketingPage';
import UserProfilePage from './pages/admin/UserProfilePage';

// Customer Facing Pages
import LandingPage from './pages/customer/LandingPage';
import MenuPage from './pages/customer/MenuPage';
import ProductDetail from './pages/customer/ProductDetail';
import CheckoutPage from './pages/customer/CheckoutPage';
import OrderTrackingPage from './pages/customer/OrderTrackingPage';
import OrderHistoryPage from './pages/customer/OrderHistoryPage';
import { isSupabaseConfigured } from './services/supabaseClient';
import { X } from 'lucide-react';

// Wrapper to provide Cart context only to customer-facing pages
const CustomerApp: React.FC = () => (
    <CartProvider>
        <Outlet />
    </CartProvider>
);

const ConfigurationErrorScreen: React.FC = () => {
    return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-brand-dark text-brand-white font-sans green-ops-grid">
            <div className="w-full max-w-2xl bg-brand-light border-2 border-red-500/50 rounded-3xl p-10 text-center shadow-2xl animate-fade-in-up">
                <div className="w-16 h-16 bg-red-500/10 rounded-2xl flex items-center justify-center text-red-500 mx-auto mb-6 border border-red-500/20">
                  <X size={32}/>
                </div>
                <h1 className="text-3xl font-black text-red-400 uppercase mb-4">Configuration Error</h1>
                <p className="text-gray-300 mb-8">The application cannot connect to the backend. Please set up your frontend environment variables.</p>
                <div className="bg-brand-dark p-6 rounded-xl text-left font-mono text-sm text-gray-400 border border-white/10">
                    <p className="mb-4">Create a <strong className="text-white">.env</strong> file in the project root and add your Supabase keys:</p>
                    <pre className="whitespace-pre-wrap bg-black/30 p-4 rounded-lg">
                        VITE_SUPABASE_URL=YOUR_SUPABASE_URL
                        <br/>
                        VITE_SUPABASE_ANON_KEY=YOUR_SUPABASE_ANON_KEY
                    </pre>
                    <p className="mt-6 text-gray-500">Note: For AI and payment features to work, you must also set your API keys in your Supabase project's Edge Function environment variables.</p>
                </div>
                <p className="text-gray-500 mt-8 text-xs font-bold uppercase tracking-widest">After adding the .env file, please restart the application.</p>
            </div>
        </div>
    );
};

const App: React.FC = () => {
  if (!isSupabaseConfigured) {
    return <ConfigurationErrorScreen />;
  }

  return (
    <AuthProvider>
      <ToastProvider>
        <StoreProvider>
          <BrowserRouter>
            <Routes>
              {/* SaaS, SEO, & Auth Routes */}
              <Route path="/" element={<SaaSHomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/setup" element={<ProtectedRoute><SetupRestaurant /></ProtectedRoute>} />
              <Route path="/politica-de-privacidade" element={<PrivacyPolicyPage />} />
              <Route path="/mapa-do-site" element={<SitemapPage />} />

              {/* Protected Admin Panel Routes */}
              <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<ManagementAI />} />
                <Route path="menu" element={<MenuManager />} />
                <Route path="pedidos" element={<OrdersKanban />} />
                <Route path="marketing" element={<MarketingPage />} />
                <Route path="configuracoes" element={<UserProfilePage />} />
              </Route>

              {/* Customer Facing Restaurant Routes (with Cart) */}
              <Route path="/:slug" element={<CustomerApp />}>
                  <Route index element={<LandingPage />} />
                  <Route path="menu" element={<MenuPage />} />
                  <Route path="product/:productId" element={<ProductDetail />} />
                  <Route path="checkout" element={<CheckoutPage />} />
                  <Route path="track/:orderId" element={<OrderTrackingPage />} />
                  <Route path="pedidos" element={<ProtectedRoute><OrderHistoryPage /></ProtectedRoute>} />
              </Route>
              
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </StoreProvider>
      </ToastProvider>
    </AuthProvider>
  );
};

export default App;
